<template>
  <div class="wrapper">
    <!-- Labels -->
    <div class="lines mb-1">
      <div class="account-code">
        <strong class="text-secondary-light">
          {{ $t('apps.accounting.label.select_account') }}
        </strong>
      </div>
      <div class="debit-credit">
        <div class="d-flex">
          <strong
            :class="[labelTotalColor]"
            class="text-secondary-light w-50 text-right"
          >
            {{ getNumber(totalDebit) }}
            {{ $t('apps.accounting.label.debit') }}
          </strong>
          <strong
            :class="[labelTotalColor]"
            class="text-secondary-light w-50 text-right"
          >
            {{ getNumber(totalCredit) }}
            {{ $t('apps.accounting.label.credit') }}
          </strong>
        </div>
      </div>
      <div class="label">
        <strong class="text-secondary-light">
          {{ $t('apps.accounting.label.label') }}
        </strong>
      </div>
      <div
        v-if="!disabled"
        class="delete">
        &nbsp;
      </div>
    </div>
    <!-- Form -->
    <div class="form">
      <div
        v-for="(line, lineIndex) in $v.form.lines.$each.$iter"
        :key="lineIndex"
        class="lines mb-3"
      >
        <div class="account-code">
          <vue-bootstrap-autocomplete
            v-model="line.$model.query"
            :data="filterAccountOptions"
            :serializer="(item) => item.text"
            :minMatchingChars="0"
            :showCreate="false"
            :showOnFocus="true"
            :maxMatches="500"
            :state="(() => {
              if(line.account_code.$error && lineIndex == 0) {
                return false
              }
              return null
            })()"
            :disabled="disabled"
            @input="handleInputAccount(lineIndex, $event)"
            @hit="handleSelectAccount(lineIndex, $event)"
            @keydown="handleKeydownAccount(lineIndex, $event)"
            @keyup="handleKeyupAccount(lineIndex, $event)"
            @inputBlur="handleInputBlurAccount(lineIndex, $event)"
          >
            <template
              slot="suggestion"
              slot-scope="{ data, htmlText }">
              <div class="d-flex align-items-center">
                <div class="d-flex flex-column">
                  {{ data.text }}
                </div>
              </div>
            </template>
          </vue-bootstrap-autocomplete>
        </div>
        <div class="debit-credit">
          <div class="input-group">
            <input
              v-model="line.$model.debit"
              :disabled="!line.$model.account_code || Number(line.$model.credit) != 0 || disabled"
              type="text"
              class="form-control text-right text-select-all"
              @keypress="numberOnly"
              @keyup="line.$model.debit = setToValidNumber(line.$model.debit)"
              @blur="line.$model.debit = setToCustomDigitsNumber(line.$model.debit)"
            >
            <input
              v-model="line.$model.credit"
              :disabled="!line.$model.account_code || Number(line.$model.debit) != 0 || disabled"
              type="text"
              class="form-control text-right text-select-all"
              @keypress="numberOnly"
              @keyup="line.$model.credit = setToValidNumber(line.$model.credit)"
              @blur="line.$model.credit = setToCustomDigitsNumber(line.$model.credit)"
            >
          </div>
        </div>
        <div class="label">
          <BaseInput
            v-if="!line.$model.account_code || disabled"
            :value="getMasterDataText(line.$model.label, $t)"
            :disabled="true"
          />
          <BaseInput
            v-else
            v-model="line.$model.label"
            :disabled="false"
          />
        </div>
        <div
          v-if="!disabled"
          class="delete">
          <BaseButton
            :disabled="disabled"
            class="w-100"
            @click="removeLine(lineIndex)"
          >
            <i class="far fa-trash-alt"/>
          </BaseButton>
        </div>
      </div>
    </div>
    <div class="actions mt-3">
      <BaseButton
        v-if="!disabled"
        @click="addLine()"
      >
        {{ $t('apps.accounting.button.addjournalentryline') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getMasterDataText } from '@/helpers'
import { required, minLength } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'

export default {
  props: {
    lines: {
      type: Array,
      default: () => ({}),
    },
    isValidated: false,
    disabled: Boolean,
  },

  data() {
    return {
      getMasterDataText,
      filterAccountOptions: [],
      form: {
        lines: [],
      },
    }
  },

  computed: {
    ...mapGetters({
      chartOfAccountsList: 'chartOfAccountsList',
    }),
    accountOptions() {
      return this.chartOfAccountsList.map((account) => {
        const accountType = account.account_type
        const accountCode = account.account_code
        const accountName = getMasterDataText(account.account_name, this.$t)
        return {
          text: `${accountCode} - ${accountName}`,
          value: accountCode,
          account_type: accountType,
          account_code: accountCode,
          account_name: accountName,
        }
      })
    },
    totalDebit() {
      return this.form.lines.reduce((acc, cur) => acc + Number(cur.debit), 0)
    },
    totalCredit() {
      return this.form.lines.reduce((acc, cur) => acc + Number(cur.credit), 0)
    },
    labelTotalColor() {
      if (this.$v.isValidDebitCredit.$invalid && this.$v.isValidDebitCredit.$dirty) {
        return 'text-danger'
      }
      if (!this.$v.isValidDebitCredit.$invalid && this.$v.isValidDebitCredit.isValid) {
        return 'text-success'
      }

      return null
    },
  },

  validations: {
    form: {
      lines: {
        required,
        minLength: minLength(1),
        $each: {
          account_code: {
            required: function (value) {
              const isSelectedSomeAccount = this.form.lines.some((line) => line.account_code)
              return isSelectedSomeAccount || value
            },
          },
        },
      },
    },
    isValidDebitCredit: {
      isValid: function () {
        const isSelectedSomeAccount = this.form.lines.some((line) => line.account_code)
        const fillAnyNumber =
          this.form.lines.some((line) => Number(line.debit) != 0) ||
          this.form.lines.some((line) => Number(line.credit) != 0)
        return isSelectedSomeAccount && fillAnyNumber && this.totalDebit == this.totalCredit
      },
    },
  },

  watch: {
    'form.lines': {
      handler: function () {
        const lines = this.form.lines.filter((line) => line.account_code)
        this.$emit('update:lines', lines)
      },
      deep: true,
    },
    isValidated() {
      if (this.isValidated) {
        this.$v.$touch()
      }
    },
  },

  methods: {
    addLine() {
      this.form.lines.push({
        query: '',
        account_type: '',
        account_code: '',
        debit: this.setToCustomDigitsNumber(0),
        credit: this.setToCustomDigitsNumber(0),
        label: '',
      })
    },
    removeLine(lineIndex) {
      this.form.lines = this.form.lines.filter((item, itemIndex) => itemIndex != lineIndex)
      if (!this.form.lines.length) {
        setTimeout(() => {
          this.addLine()
        }, 100)
      }
    },
    handleInputAccount(lineIndex, event) {
      this.filterAccountOptions = this.accountOptions.filter((option) => {
        const isNumber = !Number.isNaN(Number(event))
        if (isNumber) {
          return `${option.text}`.toLowerCase().startsWith(`${event}`.toLowerCase())
        }
        return `${option.text}`.toLowerCase().search(event) >= 0
      })
    },
    handleSelectAccount(lineIndex, event) {
      const option = event
      this.form.lines[lineIndex].account_code = option.account_code
      this.form.lines[lineIndex].account_type = option.account_type
    },
    handleInputBlurAccount(lineIndex, event) {
      if (!this.form.lines[lineIndex].account_code) {
        this.form.lines[lineIndex].query = ''
      }

      this.filterAccountOptions = this.accountOptions
    },
    handleKeydownAccount(lineIndex, event) {
      if (this.form.lines[lineIndex].account_code) {
        event.preventDefault()
      }
    },
    handleKeyupAccount(lineIndex, event) {
      if (event.key === 'Backspace' && this.form.lines[lineIndex].account_code) {
        this.form.lines[lineIndex].query = ''
      }

      if (!this.form.lines[lineIndex].query) {
        this.form.lines[lineIndex].account_code = ''
        this.form.lines[lineIndex].account_type = ''
      }
    },
  },

  created() {
    if (this.lines.length) {
      this.form.lines = cloneDeep(this.lines)
    } else {
      this.addLine()
      this.addLine()
    }
  },

  mounted() {
    this.filterAccountOptions = this.accountOptions
  },
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
}

.form {
  display: flex;
  flex-direction: column;
}

.lines {
  display: flex;
  gap: 10px;
}
.lines:last-of-type {
  margin-bottom: 0 !important;
}

.lines .account-code,
.lines .debit-credit,
.lines .label {
  flex: 1;
}

.lines .delete {
  width: 50px;
}

.actions {
  display: flex;
}
</style>



// WEBPACK FOOTER //
// src/components/custom/JournalEntryLine.vue